import {
    ChartBarIcon,
    CheckCircleIcon,
    PhoneIcon,
    PlusIcon,
    UserGroupIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import AgentPhone from '../components/AgentPhone';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { assignmentsAPI } from '../services/assignments';
import { contactsAPI } from '../services/contacts';

const AgentDashboard = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { addListener, isConnected } = useWebSocket();
  const [callingContact, setCallingContact] = useState(null);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [newLead, setNewLead] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    company: '',
    title: ''
  });
  const [dateFilter, setDateFilter] = useState('7d');

  // Fetch assigned leads count
  const { data: leadsData, isLoading: leadsLoading } = useQuery({
    queryKey: ['my-leads-count', dateFilter],
    queryFn: () => assignmentsAPI.getMyLeads({ status: 'pending' }),
    refetchInterval: 30000,
  });

  // Note: Call statistics and history will be fetched from calls API
  // For now, we'll disable these queries until we add the endpoints
  const statsData = { stats: {} };
  const statsLoading = false;
  const callsData = { calls: [] };

  // Add manual lead mutation
  const addLeadMutation = useMutation({
    mutationFn: (data) => {
      // Transform data to match API expectations
      const apiData = {
        first_name: data.firstName,
        last_name: data.lastName || '',
        phone: data.phone,
        email: data.email || '',
        company: data.company || '',
        title: data.title || '',
        campaign_id: '' // Will use default campaign on backend
      };
      return contactsAPI.createContact(apiData);
    },
    onSuccess: (response) => {
      toast.success('Lead added successfully');
      setShowAddLeadModal(false);
      setNewLead({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        company: '',
        title: ''
      });
      // Immediately call the new lead
      if (response.contact) {
        setCallingContact({
          id: response.contact.id,
          firstName: response.contact.firstName,
          lastName: response.contact.lastName,
          phone: response.contact.phone,
          company: response.contact.company,
          title: response.contact.title
        });
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to add lead');
    },
  });

  // Set up WebSocket listeners for real-time updates
  useEffect(() => {
    if (!isConnected || !user?.id) return;

    const handleCallUpdate = (_data) => {
      // Refresh call data when call status changes
      queryClient.invalidateQueries({ queryKey: ['my-calls'] });
      queryClient.invalidateQueries({ queryKey: ['call-stats'] });
    };

    const handleLeadAssignment = (_data) => {
      // Refresh leads when new assignments are made
      queryClient.invalidateQueries({ queryKey: ['my-leads'] });
    };

    const handleAgentUpdate = (_data) => {
      // Refresh agent-specific data
      queryClient.invalidateQueries({ queryKey: ['my-leads'] });
      queryClient.invalidateQueries({ queryKey: ['my-calls'] });
      queryClient.invalidateQueries({ queryKey: ['call-stats'] });
    };

    addListener('call_status_update', handleCallUpdate);
    addListener('new_lead_assigned', handleLeadAssignment);
    addListener('agent_status_change', handleAgentUpdate);

    return () => {
      // Cleanup listeners when component unmounts
    };
  }, [isConnected, user?.id, addListener, queryClient]);

  // eslint-disable-next-line no-unused-vars
  const handleStartCall = (lead) => {
    // Check if already calling
    if (callingContact) {
      toast.error('A call is already in progress. Please complete the current call first.');
      return;
    }

    // Open manual call interface
    setCallingContact({
      id: lead.contact.id,
      firstName: lead.contact.firstName,
      lastName: lead.contact.lastName,
      phone: lead.contact.phone,
      company: lead.contact.company,
      title: lead.contact.title,
      organizationId: user?.organizationId
    });
  };

  const handleCallComplete = () => {
    setCallingContact(null);
    queryClient.invalidateQueries({ queryKey: ['my-leads'] });
    queryClient.invalidateQueries({ queryKey: ['call-stats'] });
    queryClient.invalidateQueries({ queryKey: ['my-calls'] });
  };

  const handleAddLead = (e) => {
    e.preventDefault();

    // Basic validation
    if (!newLead.firstName || !newLead.lastName || !newLead.phone) {
      toast.error('Please fill in first name, last name and phone number');
      return;
    }

    // Trim the phone number
    const trimmedPhone = newLead.phone.trim();

    // Validate phone number format - allow digits, spaces, +, -, (, )
    const phoneRegex = /^[+]?[\d\-() ]+$/;
    if (!phoneRegex.test(trimmedPhone)) {
      toast.error('Please enter a valid phone number (digits, +, -, (), and spaces only)');
      return;
    }

    // Clean phone number - remove spaces, dashes, parentheses but keep the +
    const cleanedPhone = trimmedPhone.replace(/[\s\-()]/g, '');

    // Count only digits (excluding + sign)
    const digitCount = cleanedPhone.replace(/\D/g, '').length;
    if (digitCount < 10) {
      toast.error('Phone number must have at least 10 digits');
      return;
    }

    // Ensure the phone number starts with + if it has a country code
    const finalPhone = cleanedPhone.startsWith('+') ? cleanedPhone : cleanedPhone;

    addLeadMutation.mutate({
      ...newLead,
      phone: finalPhone
    });
  };

  const leads = leadsData?.leads || [];
  const stats = statsData?.stats || {};
  const calls = callsData?.calls || [];

  // Get assigned leads count (for current pending leads, not filtered by date)
  const assignedLeadsCount = leads.length;

  if (leadsLoading || statsLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Manage your leads and make calls</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Date Range:</label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="1d">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
          </div>
          <button
            onClick={() => setShowAddLeadModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Lead & Call
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Assigned Leads</dt>
                  <dd className="text-lg font-medium text-gray-900">{assignedLeadsCount}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <PhoneIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Calls</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.totalCalls || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Answered Calls</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.answeredCalls || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Conversion Rate</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.conversionRate || 0}%</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Calls */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Recent Calls</h3>
          {calls.length === 0 ? (
            <div className="text-center py-8">
              <PhoneIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No calls yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Your call history will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {calls.map((call) => (
                <div key={call.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-700">
                          {call.contact?.firstName?.charAt(0) || call.contactName?.charAt(0) || '?'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {call.contact ? `${call.contact.firstName} ${call.contact.lastName}` : (call.contactName || 'Unknown')}
                      </div>
                      <div className="text-sm text-gray-500">{call.contact?.phone || call.phone || '-'}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      call.outcome === 'scheduled' || call.outcome === 'interested'
                        ? 'bg-green-100 text-green-800'
                        : call.outcome === 'not_interested'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {call.outcome || call.status || 'N/A'}
                    </span>
                    <div className="text-sm text-gray-500">
                      {call.duration ? `${Math.floor(call.duration / 60)}:${(call.duration % 60).toString().padStart(2, '0')}` : '-'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {new Date(call.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Lead Modal */}
      {showAddLeadModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add New Lead</h3>
              <button
                onClick={() => setShowAddLeadModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleAddLead} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newLead.firstName}
                  onChange={(e) => setNewLead({ ...newLead, firstName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newLead.lastName}
                  onChange={(e) => setNewLead({ ...newLead, lastName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={newLead.phone}
                  onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+1234567890"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={newLead.email}
                  onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company
                </label>
                <input
                  type="text"
                  value={newLead.company}
                  onChange={(e) => setNewLead({ ...newLead, company: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={newLead.title}
                  onChange={(e) => setNewLead({ ...newLead, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={addLeadMutation.isLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
                >
                  {addLeadMutation.isLoading ? 'Adding...' : 'Add & Call'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddLeadModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manual Call Interface Modal */}
      {callingContact && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <AgentPhone
            contact={callingContact}
            onClose={() => {
              setCallingContact(null);
              queryClient.invalidateQueries({ queryKey: ['my-leads'] });
              queryClient.invalidateQueries({ queryKey: ['call-stats'] });
              queryClient.invalidateQueries({ queryKey: ['my-calls'] });
            }}
            onCallComplete={handleCallComplete}
          />
        </div>
      )}
    </div>
  );
};

export default AgentDashboard;
